import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { AlertTriangle, RotateCw } from 'lucide-react'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Short label for what failed, e.g. "Needs Attention" — shown in the fallback. */
  label?: string
  /** Compact inline fallback for a single widget (vs a full page). */
  compact?: boolean
  /**
   * When this value changes, the boundary clears its error and re-renders its
   * children. Used to auto-recover on route changes so a crash on one page does
   * not stick after the user navigates away.
   */
  resetKey?: unknown
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Generic React error boundary for the command centre. A render error in any
 * child is caught here and replaced with a small inline "couldn't load"
 * fallback, so one failing record/widget degrades a single card instead of
 * white-screening the whole internal tool. Error boundaries must be class
 * components — there is no hook equivalent.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null })
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface to the console for Axiom/Sentry breadcrumbs. The error message and
    // component stack are non-PII (component names + a TypeError message).
    console.error(
      `[dashboard] ${this.props.label ?? 'A section'} failed to render:`,
      error,
      info.componentStack,
    )
  }

  private reset = () => this.setState({ error: null })

  render() {
    if (this.state.error) {
      return (
        <ErrorFallback
          label={this.props.label}
          compact={this.props.compact}
          onRetry={this.reset}
        />
      )
    }
    return this.props.children
  }
}

function ErrorFallback({
  label,
  compact,
  onRetry,
}: {
  label?: string
  compact?: boolean
  onRetry: () => void
}) {
  const what = label ?? 'This section'
  return (
    <div
      role="alert"
      className={`flex items-start gap-3 bg-cmd-error-container ${compact ? 'p-3' : 'p-4'}`}
    >
      <AlertTriangle
        className={`shrink-0 text-cmd-on-error ${compact ? 'mt-0.5 h-4 w-4' : 'mt-0.5 h-5 w-5'}`}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className={`font-label text-cmd-on-error ${compact ? 'text-xs' : 'text-sm'}`}>
          {what} couldn’t load.
        </p>
        <p className="mt-0.5 font-label text-[0.65rem] text-cmd-on-error/70">
          The rest of the dashboard is unaffected.
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="flex shrink-0 items-center gap-1 font-label text-[0.65rem] tracking-wider text-cmd-on-error/90 hover:text-cmd-on-error focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cmd-on-error"
      >
        <RotateCw className="h-3 w-3" aria-hidden="true" />
        RETRY
      </button>
    </div>
  )
}

/**
 * Route-level boundary: wraps the active page so a crash shows the fallback
 * instead of a blank screen, and auto-recovers when the user navigates to a
 * different route (the path becomes the reset key).
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation()
  return (
    <ErrorBoundary label="This page" resetKey={location.pathname}>
      {children}
    </ErrorBoundary>
  )
}
